import { FC, PropsWithChildren } from 'react';

import { useCreateDraftProtectPolicyMutation } from '@graphql/requests';

import { useAnalyticsReportingService } from '@ui-furniture/common/analytics';
import { ReservationAgreementQuestionnaireData } from '@ui-furniture/common/questionnaires/use-ra-questionnaire';
import { useEnsureUserHasProfileAndIsAuthenticated } from '@ui-furniture/hooks/use-create-visitor-profile';
import { useLoadingCallback } from '@ui-furniture/hooks/use-loading-callback';

import { RAContext } from './ra.context';

export const RAProvider: FC<PropsWithChildren> = ({ children }) => {
  const [createDraftProtectPolicy] = useCreateDraftProtectPolicyMutation();
  const { reportEvent } = useAnalyticsReportingService();
  const ensureUserHasProfileAndIsAuthenticated =
    useEnsureUserHasProfileAndIsAuthenticated();

  const [isCreating, createDraftPolicy] = useLoadingCallback(
    async (data: ReservationAgreementQuestionnaireData) => {
      await ensureUserHasProfileAndIsAuthenticated();
      const result = await createDraftProtectPolicy({
        variables: {
          data: {
            ownerSide: data.ownerSide,
            addressId: data.addressId,
            template: {
              depositAmountInPounds: data.depositAmountInPounds!,
              additionalConditions: data.additionalConditions,
            },
          },
        },
      });

      const draftPolicy = result.data?.draftPolicy;
      if (!draftPolicy) throw new Error('Draft policy was not created');

      reportEvent({
        name: 'Protect - Draft Created',
        ...data,
      });
      return draftPolicy;
    },
    []
  );

  return (
    <RAContext.Provider
      value={{
        isCreating,
        createDraftPolicy,
      }}
    >
      {children}
    </RAContext.Provider>
  );
};
